import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t, i18n } from '../core/i18n.js';
import { escapeHtml, formatSmartDate } from '../utils/formatters.js';
import { chaosNotify } from '../utils/alerts.js';
import {
    scrollToBottom, scrollIfNearBottom, repondre, commencerEdition,
    setReactionMessageId, ouvrirMenuReaction, ouvrirFullEmojiDepuisBtn,
    construireReactionsMessage,
    renderVoiceBubble, renderVideoBubble, renderImageBubble, renderFileBubble,
    renderSphereInvite, renderPoll
} from './chat.js';
import { getNotifPref, ouvrirModalReport } from './modals.js';
import { appliquerEmojisCustom } from '../core/customEmojis.js';
import { applyTwemoji, initTwemojiPickers } from '../core/twemoji-setup.js';
import { triggerMessageEffect } from '../core/messageEffects.js';
import { _textBeforeCursor } from './chatInput.js';
import { majFavicon } from '../core/favicon.js';
import { notifInApp } from './inAppNotif.js';
import { initWebPush } from '../core/webPush.js';

// Alias same-ref — mutations visibles dans tous les modules
const utilisateurs = state.utilisateurs;


const notifSound = new Audio('./sounds/notif.mp3');

// ==========================================================================
//   COULEURS & NOM D'AFFICHAGE
// ==========================================================================

export function getPseudoColor(pseudo, couleurBDD = null) {
    if (couleurBDD && couleurBDD.trim() !== "") return couleurBDD;
    let hash = 0;
    for (let i = 0; i < pseudo.length; i++) {
        hash = pseudo.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash) % 360}, 70%, 60%)`;
}

// Nom à AFFICHER : nom_affiche si défini, sinon le pseudo.
// Retour échappé (sûr pour innerHTML).
export function nomAfficheDe(ref) {
    let val;
    if (ref && typeof ref === 'object') val = ref.nom_affiche || ref.pseudo || '';
    else val = (utilisateurs[ref] && utilisateurs[ref].nom_affiche) || ref || '';
    return escapeHtml(val);
}

// ==========================================================================
//   POPUP INFO BADGE (clic sur badge de message)
// ==========================================================================

const _badgePopup = document.getElementById('badge-info-popup');

function _showBadgePopup(badge, anchorEl) {
    if (!_badgePopup || !badge) return;
    _badgePopup.innerHTML = '';

    if (badge.sphereIcone) {
        const img = document.createElement('img');
        img.src = badge.sphereIcone;
        img.className = 'bip-icone';
        _badgePopup.appendChild(img);
    }
    const nom = document.createElement('div');
    nom.className = 'bip-nom';
    nom.textContent = badge.sphereNom;
    _badgePopup.appendChild(nom);

    const pill = document.createElement('span');
    pill.className = 'bip-pill';
    pill.textContent = badge.label;
    pill.style.background = badge.color;
    _badgePopup.appendChild(pill);

    if (badge.sphereDesc) {
        const desc = document.createElement('div');
        desc.className = 'bip-desc';
        desc.textContent = badge.sphereDesc;
        _badgePopup.appendChild(desc);
    }

    _badgePopup.style.display = 'block';
    const r = anchorEl.getBoundingClientRect();
    let left = r.left;
    let top  = r.bottom + 6;
    const pw = _badgePopup.offsetWidth || 200;
    const ph = _badgePopup.offsetHeight || 120;
    if (left + pw > window.innerWidth - 8) left = window.innerWidth - pw - 8;
    if (top + ph > window.innerHeight - 8) top = r.top - ph - 6;
    _badgePopup.style.left = left + 'px';
    _badgePopup.style.top  = top  + 'px';
}

document.addEventListener('click', (e) => {
    if (!e.target.closest('#badge-info-popup') && !e.target.closest('.msg-badge')) {
        if (_badgePopup) _badgePopup.style.display = 'none';
    }
});

// ==========================================================================
//   RÉCUPÉRATION D'INFOS AGENT À LA VOLÉE
// ==========================================================================

const _infosAgentDemandees = new Set();

export function assurerInfosAgent(pseudo) {
    if (!pseudo || pseudo === state.monPseudo) return;
    if (utilisateurs[pseudo]) return;
    if (_infosAgentDemandees.has(pseudo)) return;
    _infosAgentDemandees.add(pseudo);
    socket.emit('get-user-render-info', pseudo);
}

socket.on('user-render-info', (info) => {
    if (!info || !info.pseudo) return;
    utilisateurs[info.pseudo] = info;
    const coul = getPseudoColor(info.pseudo, info.couleur);
    const aff = nomAfficheDe(info.pseudo);
    document.querySelectorAll(`.message-author[data-pseudo="${CSS.escape(info.pseudo)}"]`).forEach(el => {
        el.style.color = coul;
        el.innerHTML = aff;
    });
});

// ==========================================================================
//   MESSAGES À LA CHAÎNE — groupement façon Discord
// ==========================================================================

const GROUP_WINDOW_MS = 7 * 60 * 1000;

function _memeJour(a, b) {
    const da = new Date(a), db = new Date(b);
    return da.getFullYear() === db.getFullYear()
        && da.getMonth() === db.getMonth()
        && da.getDate() === db.getDate();
}

function _doitGrouper(prevLi, data) {
    if (!prevLi || !prevLi.classList || !prevLi.classList.contains('message-item')) return false;
    if (prevLi.dataset.e2eePlaceholder) return false;
    if (data.replyTo && data.replyTo.auteur) return false;
    if ((prevLi.dataset.auteur || '') !== (data.auteur || '')) return false;
    const prevTs = Number(prevLi.dataset.ts || 0);
    const curTs = new Date(data.date_envoi || Date.now()).getTime();
    if (!prevTs || !curTs) return false;
    if (Math.abs(curTs - prevTs) > GROUP_WINDOW_MS) return false;
    if (!_memeJour(prevTs, curTs)) return false;
    return true;
}

export function _recalcGroupeNoeud(sib) {
    if (!sib || !sib.classList || !sib.classList.contains('message-item')) return;
    const fake = {
        auteur: sib.dataset.auteur || '',
        date_envoi: Number(sib.dataset.ts) || 0,
        replyTo: sib.querySelector(':scope > .message-body > .message-reply-quote') ? { auteur: '_' } : null
    };
    if (_doitGrouper(sib.previousElementSibling, fake)) sib.classList.add('message-grouped');
    else sib.classList.remove('message-grouped');
}

// ==========================================================================
//   AJOUT D'UN MESSAGE AU DOM
// ==========================================================================

export function ajouterMessageAuDOM(data, opts = {}) {
    const messagesUl = document.getElementById('messages');
    if (!messagesUl) return;
    const prepend = !!opts.prepend;

    // Dédup par id numérique (salon) puis par message_logical_id (E2EE)
    if (data.id && String(data.id).indexOf('local-') !== 0) {
        if (messagesUl.querySelector(`.message-item[data-msg-id="${CSS.escape(String(data.id))}"]`)) return;
    }
    const lid = data.message_logical_id || null;
    if (lid) {
        const sel = `.message-item[data-logical-id="${CSS.escape(lid)}"]`;
        const existants = messagesUl.querySelectorAll(sel);
        if (!data._isE2eePlaceholder) {
            existants.forEach(el => { if (el.dataset.e2eePlaceholder) el.remove(); });
            if (messagesUl.querySelector(sel + ':not([data-e2ee-placeholder])')) return;
        } else if (existants.length > 0) {
            return;
        }
    }

    const li = document.createElement('li');
    li.className = 'message-item';
    li.dataset.msgId = data.id;
    if (lid) li.dataset.logicalId = lid;
    if (data._isE2eePlaceholder) li.dataset.e2eePlaceholder = data.auteur;
    const _curTs = new Date(data.date_envoi || Date.now()).getTime();
    li.dataset.auteur = data.auteur || '';
    li.dataset.ts = String(_curTs);
    li.dataset.texte = data.texte || '';
    const _heureCourte = new Date(_curTs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const msgSalonId = data.salon_id || data.salonId || null;
    const dest = (data.destinataire === "public" || !data.destinataire) ? "public" : data.destinataire;

    let doitAfficher = false;
    if (state.salonActuelID !== null) {
        if (msgSalonId && String(msgSalonId) === String(state.salonActuelID)) doitAfficher = true;
    } else if (state.destinataireID !== null) {
        const estLie = (data.auteur === state.destinataireID || data.destinataire === state.destinataireID);
        const estBotMpLie = data.isBot && data.mpAuteur &&
            (data.mpAuteur === state.destinataireID || data.destinataire === state.destinataireID);
        if ((estLie || estBotMpLie) && !msgSalonId) doitAfficher = true;
    } else if (!state.sphereActuelleID) {
        if (dest === "public" && !msgSalonId) doitAfficher = true;
    }

    if (!doitAfficher) return;

    const heureAffichee = formatSmartDate(data.date_envoi || new Date());
    const couleur = getPseudoColor(data.auteur, data.couleur_bdd || data.couleur);
    const initiale = data.auteur ? data.auteur.charAt(0).toUpperCase() : '?';

    if (data.auteur && !data.isWebhook && !utilisateurs[data.auteur]) {
        assurerInfosAgent(data.auteur);
    }

    const infosAuteur = utilisateurs[data.auteur] || {};
    const urlAvatar = data.avatar_url || infosAuteur.avatar_url;
    let htmlAvatar = '';

    if (urlAvatar && urlAvatar !== 'none' && String(urlAvatar) !== 'undefined') {
        htmlAvatar = `<div class="message-avatar avatar-clickable" data-username="${data.auteur}" style="background: url('${urlAvatar}') center/cover no-repeat; border: 2px solid ${couleur}; color: transparent;"></div>`;
    } else {
        htmlAvatar = `<div class="message-avatar avatar-clickable" data-username="${data.auteur}" style="background-color:${couleur}">${initiale}</div>`;
    }

    let quoteHtml = '';
    if (data.replyTo && data.replyTo.auteur) {
        const clocheNotif = data.replyTo.notifier ? '🔔 ' : '';
        quoteHtml = `
            <div class="message-reply-quote">
                <span class="reply-author-name">${clocheNotif}@${data.replyTo.auteur}</span>
                <span class="reply-text-preview">${data.replyTo.texte}</span>
            </div>
        `;
    }

    li.innerHTML = `
        <div class="message-left">
            ${htmlAvatar}
            <span class="message-hover-time">${_heureCourte}</span>
        </div>
        <div class="message-body">
            ${quoteHtml}
            <div class="message-header">
                <span class="message-author" style="color:${couleur}" data-pseudo="${escapeHtml(data.auteur)}">${nomAfficheDe(data.auteur)}</span>
                ${data.isWebhook ? '<span class="webhook-tag">WEBHOOK</span>' : ''}
                ${(data.badge || utilisateurs[data.auteur]?.badge) ? (() => { const b = data.badge || utilisateurs[data.auteur].badge; return `<span class="msg-badge" style="--badge-color:${b.color}" data-badge="${encodeURIComponent(JSON.stringify(b))}">${b.label}</span>`; })() : ''}
                <span class="message-time">${heureAffichee}</span>
            </div>
            <div class="message-content markdown-body"></div>
            ${data.expire_at ? `<div class="ephemere-badge" data-expire="${new Date(data.expire_at).getTime()}">⏱ <span class="ephemere-countdown"></span></div>` : ''}
            <div class="message-reactions-container" id="reactions-for-${data.id}"></div>
        </div>
        <div class="message-actions-bar">
            <button class="action-bar-btn btn-quick-react" data-emoji="❤️" title="J'adore">❤️</button>
            <button class="action-bar-btn btn-quick-react" data-emoji="😂" title="Mort de rire">😂</button>
            <button class="action-bar-btn btn-quick-react" data-emoji="😭" title="Triste">😭</button>
            <button class="action-bar-btn btn-quick-react" data-emoji="👍" title="Pouce">👍</button>
            <button class="action-bar-btn btn-reagir-plus" title="${t('ctx.msg.react')}"><svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14"><path d="M8 2.5a.75.75 0 0 1 .75.75v4h4a.75.75 0 0 1 0 1.5h-4v4a.75.75 0 0 1-1.5 0v-4h-4a.75.75 0 0 1 0-1.5h4v-4A.75.75 0 0 1 8 2.5Z" fill="currentColor"/></svg></button>
            <div class="action-bar-sep"></div>
            <button class="action-bar-btn btn-repondre" title="${t('ctx.msg.reply')}"><i class="fas fa-reply"></i></button>
            ${data.auteur === state.monPseudo ? `<button class="action-bar-btn btn-modifier" title="${t('ctx.msg.edit')}"><i class="fas fa-pen"></i></button>` : ''}
        </div>
    `;

    li.querySelector('.msg-badge')?.addEventListener('click', (e) => {
        e.stopPropagation();
        try { _showBadgePopup(JSON.parse(decodeURIComponent(e.currentTarget.dataset.badge)), e.currentTarget); } catch(_) {}
    });

    const messageContent = li.querySelector('.message-content');
    let contenu = data.texte || data.contenu || "";

    if (contenu.startsWith('{{') && contenu.endsWith('}}')) {
        const cleI18n = contenu.substring(2, contenu.length - 2);
        contenu = t(cleI18n);
    } else if (contenu.startsWith('{') && (contenu.includes('"isI18n"') || contenu.includes('"isI18nArray"'))) {
        try {
            const dataI18n = JSON.parse(contenu);
            if (dataI18n.isI18nArray) {
                const listePhrases = i18n.tArray(dataI18n.key);
                contenu = listePhrases[Math.floor(Math.random() * listePhrases.length)];
            } else {
                contenu = t(dataI18n.key, dataI18n.params);
            }
        } catch (e) {
            console.error("Erreur de traduction B.O.B :", e);
        }
    }

    if (contenu.startsWith('{') && contenu.includes('"chaosVoice"')) {
        renderVoiceBubble(messageContent, contenu);
    } else if (contenu.startsWith('{') && contenu.includes('"chaosVideo"')) {
        renderVideoBubble(messageContent, contenu);
    } else if (contenu.startsWith('{') && contenu.includes('"chaosImage"')) {
        renderImageBubble(messageContent, contenu);
    } else if (contenu.startsWith('{') && contenu.includes('"chaosFile"')) {
        renderFileBubble(messageContent, contenu);
    } else if (contenu.startsWith('{') && contenu.includes('"chaosPoll"')) {
        renderPoll(messageContent, contenu);
    } else if (contenu.startsWith('{') && contenu.includes('"chaosInvite"')) {
        renderSphereInvite(messageContent, contenu);
    } else if (contenu.includes('tenor.com') || contenu.includes('giphy.com')) {
        const img = document.createElement('img');
        img.src = contenu;
        img.className = "message-gif";
        messageContent.appendChild(img);
    } else {
        const contenuAvecEmojis = appliquerEmojisCustom(contenu, state.sphereActuelleID);
        const contenuAvecMentions = contenuAvecEmojis.replace(/@(\w{2,20})\b/g, (match, pseudo) => {
            const isSelf = pseudo.toLowerCase() === (state.monPseudo || '').toLowerCase();
            const agentInfo = utilisateurs[pseudo];
            const couleur = agentInfo?.couleur || '#58a6ff';
            const bg = couleur + '22';
            const style = `color:${couleur};background:${bg};`;
            const prefix = isSelf ? '🔔 ' : '';
            return `<span class="mention${isSelf ? ' mention-self' : ''}" data-pseudo="${pseudo}" style="${style}">${prefix}@${pseudo}</span>`;
        });
        const rawHtml = marked.parse(contenuAvecMentions);
        const cleanHtml = DOMPurify.sanitize(rawHtml, {
            ADD_ATTR: ['data-pseudo'],
            ADD_TAGS: ['img'],
            ALLOW_DATA_ATTR: false,
            ALLOWED_ATTR: ['data-pseudo', 'src', 'alt', 'title', 'class', 'loading'],
        });
        messageContent.innerHTML = cleanHtml;
        applyTwemoji(messageContent);
    }

    messageContent.addEventListener('click', (e) => {
        const img = e.target.closest('img.twemoji, img.emoji');
        if (!img || !img.alt) return;
        triggerMessageEffect(img.alt);
    });

    li.querySelector('.btn-repondre').addEventListener('click', () => {
        repondre(data.auteur, contenu);
    });
    li.querySelectorAll('.btn-quick-react').forEach(btn => {
        applyTwemoji(btn);
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            setReactionMessageId(data.id);
            envoyerReaction(btn.dataset.emoji);
        });
    });
    li.querySelector('.btn-reagir-plus').addEventListener('click', (e) => {
        e.stopPropagation();
        ouvrirFullEmojiDepuisBtn(e, data.id);
    });
    li.querySelector('.btn-modifier')?.addEventListener('click', () => {
        commencerEdition({ id: data.id, texte: contenu });
    });

    li.oncontextmenu = (e) => {
        e.preventDefault();
        e.stopPropagation();

        window.msgContextuelActuel = {
            id: data.id,
            auteur: data.auteur,
            texte: contenu,
            message_logical_id: data.message_logical_id || null
        };

        const menu = document.getElementById('message-context-menu');

        const isMine = (data.auteur === state.monPseudo);
        const peutModererCe = !isMine
            && !data.message_logical_id
            && !!state.salonActuelID
            && !!window._peutModererSalon
            && String(window._peutModererSalonId) === String(state.salonActuelID);

        document.getElementById('ctx-msg-edit').style.display = isMine ? 'block' : 'none';
        document.getElementById('ctx-msg-delete').style.display = (isMine || peutModererCe) ? 'block' : 'none';
        document.getElementById('ctx-msg-unread').style.display = isMine ? 'none' : 'block';
        document.getElementById('ctx-msg-report').style.display = isMine ? 'none' : 'block';
        document.getElementById('ctx-msg-ban').style.display = peutModererCe ? 'block' : 'none';

        menu.style.display = 'block';
        const menuW = menu.offsetWidth || 220;
        const menuH = menu.offsetHeight || 280;
        const margePx = 8;
        let left = e.clientX;
        let top = e.clientY;
        if (left + menuW > window.innerWidth - margePx) left = window.innerWidth - menuW - margePx;
        if (left < margePx) left = margePx;
        if (top + menuH > window.innerHeight - margePx) top = window.innerHeight - menuH - margePx;
        if (top < margePx) top = margePx;
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
    };

    // Préfixe (pagination « charger plus ») : on insère au-dessus de l'ancre
    // sans séparateur non-lus, sans regroupement et sans scroll vers le bas.
    if (prepend) {
        messagesUl.insertBefore(li, opts.anchor || messagesUl.firstElementChild);
        construireReactionsMessage(data.id, data.reactions, state.monPseudo);
        return;
    }

    // Position CHRONOLOGIQUE : un message récupéré (rekey/SBK) ou reçu hors-ligne
    // peut arriver APRÈS des messages plus récents (ex. appareil fraîchement
    // enregistré qui rejoue son historique en direct). On l'insère donc à sa
    // place selon date_envoi, pas systématiquement en bas. Cas courant (nouveau
    // message = le plus récent) → refNode null → ajout en bas + scroll, comme avant.
    let refNode = null; // li devant lequel insérer ; null = ajout en bas
    for (let node = messagesUl.lastElementChild; node; node = node.previousElementSibling) {
        const ts = Number(node.dataset && node.dataset.ts);
        if (!ts) continue;            // ignorer séparateurs / nœuds sans timestamp
        if (ts <= _curTs) break;      // plus ancien/égal → on insère APRÈS lui
        refNode = node;               // plus récent → on insérera AVANT lui
    }
    const enBas = (refNode === null);

    // Séparateur non-lus (placé juste avant le message concerné)
    if (premierNonVuId !== null && String(data.id) === premierNonVuId) {
        const sep = document.createElement('li');
        sep.id = 'unread-separator';
        sep.className = 'unread-separator';
        sep.innerHTML = `<span>${t('chat.new_messages')}</span>`;
        if (enBas) messagesUl.appendChild(sep);
        else messagesUl.insertBefore(sep, refNode);
        premierNonVuId = null;
    }

    // Regroupement vs le message qui PRÉCÈDE le point d'insertion.
    const prevPourGroupe = enBas ? messagesUl.lastElementChild : refNode.previousElementSibling;
    if (_doitGrouper(prevPourGroupe, data)) li.classList.add('message-grouped');

    if (enBas) messagesUl.appendChild(li);
    else messagesUl.insertBefore(li, refNode);

    construireReactionsMessage(data.id, data.reactions, state.monPseudo);
    // Le message suivant doit recalculer son regroupement (son prédécesseur a changé).
    if (refNode) _recalcGroupeNoeud(refNode);
    // On ne scrolle que si le message a été ajouté en bas (vrai nouveau message).
    if (enBas) scrollToBottom();

    // Effet uniquement pour les messages arrivés en live (opts.playEffect = true)
    if (enBas && !data._isE2eePlaceholder && opts.playEffect) {
        triggerMessageEffect(data.texte || data.contenu || '');
    }
}

// ==========================================================================
//   BADGES NON-LUS (salons + sphères dérivées)
// ==========================================================================

const salonUnreadCounts = {};
const salonMentionFlag  = {};
const salonSphere       = {};

export function enregistrerSalonSphere(salonId, sphereId) {
    if (salonId && sphereId) salonSphere[salonId] = sphereId;
}

function _appliquerUnBadgeSalon(salonId) {
    const badge = document.getElementById(`channel-badge-${salonId}`);
    if (!badge) return;
    const count = salonUnreadCounts[salonId] || 0;
    if (count <= 0) {
        badge.style.display = 'none';
        badge.classList.remove('channel-badge-mention');
        return;
    }
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.style.display = '';
    badge.classList.toggle('channel-badge-mention', !!salonMentionFlag[salonId]);
}

export function recalculerBadgeSphere(sphereId) {
    if (!sphereId) return;
    let total = 0, mention = false;
    for (const sid of Object.keys(salonUnreadCounts)) {
        if (String(salonSphere[sid]) !== String(sphereId)) continue;
        const n = salonUnreadCounts[sid] || 0;
        total += n;
        if (n > 0 && salonMentionFlag[sid]) mention = true;
    }
    const badge = document.getElementById(`sphere-badge-${sphereId}`);
    if (!badge) return;
    if (total <= 0) {
        badge.style.display = 'none';
        badge.classList.remove('sphere-badge-mention');
        return;
    }
    badge.textContent = total > 99 ? '99+' : String(total);
    badge.style.display = '';
    badge.classList.toggle('sphere-badge-mention', mention);
}

export function incrementerBadgeSalon(salonId, sphereId, isMention) {
    if (!salonId) return;
    enregistrerSalonSphere(salonId, sphereId);
    salonUnreadCounts[salonId] = (salonUnreadCounts[salonId] || 0) + 1;
    if (isMention) salonMentionFlag[salonId] = true;
    _appliquerUnBadgeSalon(salonId);
    recalculerBadgeSphere(salonSphere[salonId]);
}

export function resetBadgeSalon(salonId) {
    if (!salonId) return;
    salonUnreadCounts[salonId] = 0;
    salonMentionFlag[salonId] = false;
    _appliquerUnBadgeSalon(salonId);
    recalculerBadgeSphere(salonSphere[salonId]);
}

export function appliquerBadgesSalons() {
    Object.keys(salonUnreadCounts).forEach(_appliquerUnBadgeSalon);
    const spheresVues = new Set(Object.values(salonSphere).map(String));
    spheresVues.forEach(recalculerBadgeSphere);
}

// ==========================================================================
//   NOTIFICATIONS NATIVES (Electron) + BADGE NON-LUS sur l'icône
//   (en navigateur : pop-up Notification standard + préfixe (n) dans l'onglet)
// ==========================================================================

let _badgeDernier = -1;

function _totalNonLus() {
    let total = 0;
    if (state.notifications) {
        for (const k of Object.keys(state.notifications)) total += (state.notifications[k] || 0);
    }
    for (const sid of Object.keys(salonUnreadCounts)) total += (salonUnreadCounts[sid] || 0);
    return total;
}

// Dessine un petit badge rond rouge vif (avec bordure blanche pour qu'il
// ressorte sur n'importe quel fond de barre des tâches, sombre comme clair)
// → dataURL PNG utilisé par setOverlayIcon sous Windows.
function _dessinerBadge(n) {
    const txt = n > 9 ? '9+' : String(n);
    const c = document.createElement('canvas');
    c.width = 32; c.height = 32;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ef4444';
    ctx.beginPath(); ctx.arc(16, 16, 14, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(16, 16, 14, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${n > 9 ? 15 : 19}px sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(txt, 16, 17);
    return c.toDataURL('image/png');
}

// Met à jour le badge système (Electron) + le titre de l'onglet. Idempotent :
// ne pousse rien si le total n'a pas changé (sûr à appeler souvent / en boucle).
export function majBadgeNatif() {
    const n = _totalNonLus();
    if (n === _badgeDernier) return;
    _badgeDernier = n;
    majFavicon(n);
    try {
        const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
        document.title = n > 0 ? `(${n > 9 ? '9+' : n}) ${base}` : base;
    } catch (_) {}
    if (window.chaosNative && typeof window.chaosNative.setBadgeCount === 'function') {
        window.chaosNative.setBadgeCount(n, n > 0 ? _dessinerBadge(n) : null);
    }
    // PWA installée (Badging API) : badge sur l'icône d'accueil / barre des tâches.
    // En Electron, setAppBadge() mappe sur SetOverlayIcon Windows avec un badge
    // numérique noir (style système) qui écrase notre overlay rouge → on l'ignore.
    if (!window.chaosNative) try {
        if (navigator.setAppBadge) {
            if (n > 0) navigator.setAppBadge(n).catch(() => {});
            else if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
        }
    } catch (_) {}
}

// Aperçu lisible pour le corps de la notif (contenus riches → libellé court).
function _apercuNotif(texte) {
    if (!texte) return '';
    const s = String(texte);
    if (s.includes('tenor.com') || s.includes('giphy.com')) return 'GIF';
    if (s.startsWith('{')) {
        if (s.includes('chaosImage')) return '📷 Image';
        if (s.includes('chaosVideo')) return '🎬 Vidéo';
        if (s.includes('chaosVoice')) return '🎙️ Message vocal';
        if (s.includes('chaosFile'))  return '📎 Fichier';
        if (s.includes('chaosPoll'))  return '📊 Sondage';
        if (s.includes('chaosInvite')) return '✉️ Invitation';
    }
    return s.length > 120 ? s.slice(0, 117) + '…' : s;
}

function _notifNative(titre, texte, onClickNav) {
    const auteurInfo = state.utilisateurs[titre] || {};
    notifInApp({
        titre,
        texte,
        avatarUrl: auteurInfo.avatar_url,
        couleur:   auteurInfo.couleur,
        onClick:   onClickNav || null,
    });
}

(function initNotifsNatives() {
    // Demande la permission OS (fallback quand fenêtre non visible)
    const demanderPerm = () => {
        try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                Notification.requestPermission().then((perm) => {
                    if (perm === 'granted') initWebPush();
                }).catch(() => {});
            }
        } catch (_) {}
    };
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        window.addEventListener('pointerdown', demanderPerm, { once: true });
        window.addEventListener('keydown', demanderPerm, { once: true });
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Permission déjà accordée (PWA réouverte) → s'abonner au prochain tick
        setTimeout(() => initWebPush(), 1000);
    }
    setInterval(() => { try { majBadgeNatif(); } catch (_) {} }, 3000);
})();

// ==========================================================================
//   FLYOUT AUTOCOMPLÉTION DES MENTIONS @
// ==========================================================================

let _mentionResults    = [];
let _mentionActiveIdx  = -1;
let _mentionTokenStart = -1;
let _mentionFlyoutOuvert = false;

export function _fermerMentionFlyout() {
    const fl = document.getElementById('mention-flyout');
    if (fl) fl.style.display = 'none';
    _mentionFlyoutOuvert = false;
    _mentionResults = [];
    _mentionActiveIdx = -1;
    _mentionTokenStart = -1;
}

export function _detecterMention() {
    const fl = document.getElementById('mention-flyout');
    const input = document.getElementById('input');
    if (!fl || !input) return;
    const avant = _textBeforeCursor(input);
    const m = avant.match(/(^|\s)@([\w-]{0,20})$/);
    if (!m) { _fermerMentionFlyout(); return; }
    const q = m[2].toLowerCase();
    _mentionTokenStart = avant.length - m[2].length - 1;

    const moi = (state.monPseudo || '').toLowerCase();
    const enSphere = !!state.sphereActuelleID && state.membresSphereActuelle.size > 0;
    const sourcePseudos = enSphere
        ? [...state.membresSphereActuelle]
        : Object.keys(utilisateurs);
    const cands = sourcePseudos
        .filter(p => p && p.toLowerCase() !== moi)
        .map(p => {
            const u = utilisateurs[p] || {};
            return {
                pseudo: p,
                affiche: u.nom_affiche || p,
                couleur: u.couleur || u.couleur_preferee || '#58a6ff',
                avatar: u.avatar_url
            };
        })
        .filter(c => !q
            || c.pseudo.toLowerCase().includes(q)
            || (c.affiche && c.affiche.toLowerCase().includes(q)))
        .sort((a, b) => {
            const ap = a.pseudo.toLowerCase().startsWith(q) || a.affiche.toLowerCase().startsWith(q);
            const bp = b.pseudo.toLowerCase().startsWith(q) || b.affiche.toLowerCase().startsWith(q);
            if (ap !== bp) return ap ? -1 : 1;
            return a.affiche.localeCompare(b.affiche);
        })
        .slice(0, 6);

    if (cands.length === 0) { _fermerMentionFlyout(); return; }
    _mentionResults = cands;
    _mentionActiveIdx = 0;
    _renderMentionFlyout();
}

function _renderMentionFlyout() {
    const fl = document.getElementById('mention-flyout');
    if (!fl) return;
    fl.innerHTML = '';
    const hint = document.createElement('div');
    hint.className = 'mention-flyout-hint';
    hint.textContent = '↑↓ naviguer · ⏎/Tab insérer · Échap fermer';
    fl.appendChild(hint);
    _mentionResults.forEach((c, i) => {
        const row = document.createElement('div');
        row.className = 'mention-flyout-item' + (i === _mentionActiveIdx ? ' actif' : '');
        row.setAttribute('role', 'option');

        const av = document.createElement('div');
        av.className = 'mention-flyout-avatar';
        if (c.avatar && c.avatar !== 'none' && c.avatar !== 'undefined') {
            av.style.backgroundImage = `url('${c.avatar}')`;
        } else {
            av.style.background = c.couleur;
            av.textContent = (c.affiche || c.pseudo).charAt(0).toUpperCase();
        }

        const noms = document.createElement('div');
        noms.className = 'mention-flyout-noms';
        const a1 = document.createElement('span');
        a1.className = 'mention-flyout-affiche';
        a1.textContent = c.affiche;
        noms.appendChild(a1);
        if (c.affiche !== c.pseudo) {
            const a2 = document.createElement('span');
            a2.className = 'mention-flyout-pseudo';
            a2.textContent = '@' + c.pseudo;
            noms.appendChild(a2);
        }

        row.appendChild(av);
        row.appendChild(noms);
        row.addEventListener('mousedown', (e) => { e.preventDefault(); _choisirMention(i); });
        fl.appendChild(row);
    });
    fl.style.display = 'flex';
    _mentionFlyoutOuvert = true;
}

function _choisirMention(idx) {
    const input = document.getElementById('input');
    const c = _mentionResults[idx];
    if (!c || _mentionTokenStart < 0 || !input) { _fermerMentionFlyout(); return; }

    const sel = window.getSelection();
    if (!sel.rangeCount) { _fermerMentionFlyout(); return; }

    // Longueur du token "@query" à supprimer (ex: "@co" → 3 chars)
    const avant = _textBeforeCursor(input);
    const tokenLen = avant.length - _mentionTokenStart;
    const insertion = '@' + c.pseudo + ' ';

    // Étendre la sélection en arrière pour couvrir le token, puis remplacer
    sel.collapseToEnd();
    for (let i = 0; i < tokenLen; i++) {
        sel.modify('extend', 'backward', 'character');
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(insertion);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    _fermerMentionFlyout();
    input.focus();
    input.dispatchEvent(new Event('input'));
}

export function _mentionFlyoutKeydown(e) {
    if (!_mentionFlyoutOuvert || _mentionResults.length === 0) return false;
    if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        _mentionActiveIdx = (_mentionActiveIdx + 1) % _mentionResults.length;
        _renderMentionFlyout();
        return true;
    }
    if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation();
        _mentionActiveIdx = (_mentionActiveIdx - 1 + _mentionResults.length) % _mentionResults.length;
        _renderMentionFlyout();
        return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault(); e.stopPropagation();
        _choisirMention(_mentionActiveIdx < 0 ? 0 : _mentionActiveIdx);
        return true;
    }
    if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        _fermerMentionFlyout();
        return true;
    }
    return false;
}

// ==========================================================================
//   SOCKET — MESSAGES EN TEMPS RÉEL
// ==========================================================================

socket.on('chat message', (data) => {
    if (data.destinataire === state.monPseudo && data.auteur !== state.monPseudo && !data.isBot) {
        if (state.destinataireID !== data.auteur) {
            if (state.notifications) state.notifications[data.auteur] = (state.notifications[data.auteur] || 0) + 1;
            window.gererBulleMP(data.auteur, data.couleur_preferee || '#58a6ff');
            if (getNotifPref('mp')) {
                const monBadge = document.getElementById('self-status-badge');
                if (monBadge && !monBadge.classList.contains('status-occupe')) {
                    notifSound.play().catch(() => {});
                    _notifNative(data.auteur, data.texte, () => window.ouvrirChatPrive && window.ouvrirChatPrive(data.auteur));
                }
            }
            majBadgeNatif();
            socket.emit('demander-liste-maj');
        } else {
            socket.emit('marquer-lu', { expediteur: data.auteur, destinataire: state.monPseudo });
        }
    }

    if (data.salonId && data.sphereId && data.auteur !== state.monPseudo && !data.isBot) {
        const pasEnTrainDeRegarder = String(state.salonActuelID) !== String(data.salonId);
        if (pasEnTrainDeRegarder) {
            const isMentionTexte = !!(data.texte && new RegExp(`@${state.monPseudo}\\b`, 'i').test(data.texte));
            const isMentionReponse = !!(data.replyTo && data.replyTo.notifier
                && data.replyTo.auteur === state.monPseudo);
            const isMention = isMentionTexte || isMentionReponse;
            const prefSpheresOn = getNotifPref('spheres');
            const prefMentionsOn = getNotifPref('mentions');
            const doitNotifier = prefSpheresOn || (prefMentionsOn && isMention);

            if (doitNotifier) {
                incrementerBadgeSalon(data.salonId, data.sphereId, isMention);
                if (getNotifPref('sons')) notifSound.play().catch(() => {});
                // Notif OS native : uniquement sur @mention (choix utilisateur).
                // Clic → focus de l'app (le badge du salon indique où lire ;
                // on ne navigue pas faute de connaître le nom du salon ici).
                if (isMention) {
                    _notifNative(`${data.auteur} t'a mentionné`, data.texte, null);
                }
                majBadgeNatif();
            }
        }
    }

    ajouterMessageAuDOM(data, { playEffect: true });
});

socket.on('message-modifie-dom', (data) => {
    const messageLi = document.querySelector(`.message-item[data-msg-id="${data.id}"]`);
    if (messageLi) {
        const messageContent = messageLi.querySelector('.message-content');
        if (data.nouveauTexte.includes('tenor.com') || data.nouveauTexte.includes('giphy.com')) {
            messageContent.innerHTML = `<img src="${data.nouveauTexte}" class="message-gif">`;
        } else {
            const rawHtml = marked.parse(data.nouveauTexte);
            const cleanHtml = DOMPurify.sanitize(rawHtml);
            messageContent.innerHTML = cleanHtml;
            applyTwemoji(messageContent);
            messageContent.innerHTML += `<span style="font-size: 0.7em; color: #8b949e; font-style: italic; margin-left: 5px;">(modifié)</span>`;
        }
        messageLi.dataset.texte = data.nouveauTexte;
        if (window.msgContextuelActuel && window.msgContextuelActuel.id === data.id) {
            window.msgContextuelActuel.texte = data.nouveauTexte;
        }
        scrollIfNearBottom();
    }
});

socket.on('message-supprime-dom', (payload) => {
    const messageId = (payload && typeof payload === 'object') ? payload.id : payload;
    const logicalId = (payload && typeof payload === 'object') ? payload.message_logical_id : null;

    const cibles = new Set();
    if (messageId !== undefined && messageId !== null && messageId !== '') {
        document.querySelectorAll(`.message-item[data-msg-id="${CSS.escape(String(messageId))}"]`)
            .forEach(el => cibles.add(el));
    }
    if (logicalId) {
        document.querySelectorAll(`.message-item[data-logical-id="${CSS.escape(String(logicalId))}"]`)
            .forEach(el => cibles.add(el));
    }
    cibles.forEach(messageLi => {
        const suivant = messageLi.nextElementSibling;
        messageLi.style.transition = "all 0.3s ease";
        messageLi.style.opacity = "0";
        messageLi.style.transform = "scale(0.9)";
        setTimeout(() => {
            try { messageLi.remove(); } catch (_) {}
            try { _recalcGroupeNoeud(suivant); } catch (_) {}
        }, 300);
    });
});

socket.on('historique-salon', (messages, meta) => {
    window._peutModererSalon = !!(meta && meta.peutModerer);
    window._peutModererSalonId = meta && meta.salonId;
    calculerPremierNonVu(messages, cleConversation(state.salonActuelID, null));
    messages.forEach(msg => ajouterMessageAuDOM(msg));
});

// État de pagination de l'historique MP (« charger plus » au scroll vers le haut).
const _mpHist = { avec: null, oldest: null, loading: false, done: false };
let _mpHistScrollBound = false;
function _assurerScrollPaginationMP() {
    if (_mpHistScrollBound) return;
    const ul = document.getElementById('messages');
    if (!ul) return;
    _mpHistScrollBound = true;
    ul.addEventListener('scroll', () => {
        if (!state.destinataireID) return;                 // uniquement en MP
        if (_mpHist.loading || _mpHist.done) return;
        if (_mpHist.avec !== state.destinataireID || !_mpHist.oldest) return;
        if (ul.scrollTop > 80) return;                     // pas encore tout en haut
        _mpHist.loading = true;
        socket.emit('get-private-history', { avec: state.destinataireID, before: _mpHist.oldest });
    });
}

// Déchiffre/résout le texte de chaque message d'historique (mutation en place).
// Partagé entre la charge initiale et la pagination.
async function _preparerMessagesHistorique(messages) {
    const localMessages = state.destinataireID
        ? await ChaosKeystore.searchMessages(state.destinataireID, null)
        : [];
    const localById        = new Map(localMessages.map(m => [m.id, m]));
    const localByLogicalId = new Map(localMessages.filter(m => m.message_logical_id).map(m => [m.message_logical_id, m]));

    const [pairDevicesResp, msgBackups] = await Promise.all([
        new Promise(resolve => {
            if (!state.destinataireID || !window.ChaosMPCrypto) return resolve({ devices: [] });
            socket.emit('get-e2ee-device-info', state.destinataireID);
            socket.once('e2ee-device-info-' + state.destinataireID, resolve);
            setTimeout(() => resolve({ devices: [] }), 4000);
        }),
        (window.ChaosSessionBackup && state.destinataireID)
            ? ChaosSessionBackup.restaurerEnvois(state.destinataireID, socket).catch(() => new Map())
            : Promise.resolve(new Map())
    ]);
    const pairDevices = Array.isArray(pairDevicesResp?.devices) ? pairDevicesResp.devices : [];

    for (const msg of messages) {
        if (msg.algo_version > 0 && msg.contenu_chiffre) {
            const logicalId = msg.message_logical_id;
            if (msg.auteur === state.monPseudo) {
                const local = (logicalId && localByLogicalId.get(logicalId))
                    || localMessages.find(m => m.sent && Math.abs(m.date_envoi - new Date(msg.date_envoi).getTime()) < 15000);
                const fromBackup = logicalId && msgBackups.get(logicalId);
                msg.texte = local?.texte || fromBackup || '[message chiffré — données locales introuvables]';
                // Persister en IndexedDB si uniquement disponible via backup SBK : sans ça,
                // getSentByLogicalId() ne trouve rien et le rekey côté expéditeur échoue silencieusement.
                if (!local && fromBackup && logicalId) {
                    ChaosKeystore.saveMessage({
                        id: String(msg.id),
                        auteur: state.monPseudo,
                        interlocuteur: msg.destinataire || state.destinataireID,
                        texte: fromBackup,
                        date_envoi: new Date(msg.date_envoi).getTime(),
                        sent: true,
                        message_logical_id: logicalId
                    }).catch(() => {});
                }
            } else {
                const stored = localById.get(String(msg.id))
                    || (logicalId && localByLogicalId.get(logicalId));
                if (stored) {
                    msg.texte = stored.texte;
                } else {
                    const senderDevice = msg.device_expediteur || pairDevices[0]?.device_id;
                    let dechiffre = null;

                    // 1) Coffre SBK d'ABORD = source de vérité. Tout message déjà
                    //    déchiffré une fois (sur n'importe quel device) y est,
                    //    chiffré avec MA clé dérivée du PIN → lisible partout.
                    //    Le consulter avant Olm évite une tentative vouée à
                    //    l'échec (session non participante → BAD_MESSAGE_MAC /
                    //    BAD_MESSAGE_KEY_ID) ET le rekey inutile qui suivrait.
                    if (logicalId && msgBackups.has(logicalId)) {
                        dechiffre = msgBackups.get(logicalId);
                    }

                    // 2) Sinon, tenter le déchiffrement Olm de l'enveloppe.
                    if (!dechiffre && senderDevice) {
                        try {
                            dechiffre = await ChaosMPCrypto.dechiffrerMessage(
                                msg.contenu_chiffre, msg.auteur,
                                senderDevice, null
                            );
                            // Nouveau clair via Olm → l'ajouter au coffre SBK
                            // (n'y était pas, sinon on l'aurait pris en 1)).
                            if (logicalId && window.ChaosSessionBackup) {
                                ChaosSessionBackup.sauvegarderEnvoi(logicalId, msg.auteur, dechiffre, socket).catch(() => {});
                            }
                        } catch (e) {
                            console.warn(`[E2EE] Déchiffrement historique msg ${msg.id} (${msg.auteur}) échoué :`, e.message);
                            window.ChaosTelemetry?.report('e2ee_historique_echec', {
                                message: e.message, msgId: String(msg.id),
                                logicalId: logicalId || null, peer: msg.auteur, path: 'historique'
                            });
                        }
                    }

                    // Persister localement ce qu'on a obtenu (SBK ou Olm) →
                    // prochain affichage instantané via localById/localByLogicalId.
                    if (dechiffre) {
                        await ChaosKeystore.saveMessage({
                            id: String(msg.id), auteur: msg.auteur,
                            interlocuteur: msg.auteur, texte: dechiffre,
                            date_envoi: new Date(msg.date_envoi).getTime(),
                            message_logical_id: logicalId
                        });
                    }
                    if (!dechiffre && logicalId && _peutRedemanderRekey(msg.auteur + ':' + logicalId)) {
                        ChaosKeystore.getMeta('device_id').then(myDeviceId => {
                            socket.emit('e2ee-rekey-request', {
                                target: msg.auteur,
                                target_device_id: senderDevice || null,
                                message_logical_id: logicalId,
                                requester_device_id: myDeviceId
                            });
                        }).catch(() => {});
                    }
                    // Si on a un logicalId, un rekey vient d'être déclenché (bloc
                    // ci-dessus) → la resynchro est EN COURS, pas une perte : texte
                    // honnête + non alarmant (remplacé en place dès la réponse du
                    // rekey via la dédup data-logical-id). Le « session expirée »
                    // n'est gardé que pour l'irrécupérable (pas de logicalId).
                    msg.texte = dechiffre
                        || (logicalId
                            ? '🔄 *(resynchronisation du tunnel sécurisé…)*'
                            : '[message reçu hors-ligne — session expirée]');
                    if (!dechiffre) {
                        msg._isE2eePlaceholder = true;
                        // Sans logicalId, aucun rekey possible → c'est le vrai
                        // « session expirée » visible : à tracer absolument.
                        if (!logicalId) window.ChaosTelemetry?.report('e2ee_irrecuperable', {
                            message: 'placeholder session expirée (pas de logical_id)',
                            msgId: String(msg.id), peer: msg.auteur, path: 'historique'
                        });
                    }
                }
            }
        }
    }
    return messages;
}

// Throttle PERSISTANT des rekeys déclenchés depuis l'historique. Avant, le
// dédup vivait en mémoire vive (window._rekeyFromHistory) → vidé à CHAQUE
// rechargement → tous les messages encore non déchiffrés re-déclenchaient un
// rekey → tempête qui drainait les OTK. On mémorise désormais la dernière
// demande par message dans localStorage : pas de nouvelle demande avant le
// cooldown. (Avec la fallback key, le 1er rekey aboutit et met le message en
// cache → il ne repassera plus jamais ici ; ce throttle ne sert que pour la
// transition + les messages réellement irrécupérables.)
const REKEY_COOLDOWN_MS = 30 * 60 * 1000;
function _peutRedemanderRekey(key) {
    try {
        const now = Date.now();
        const map = JSON.parse(localStorage.getItem('e2ee_rekey_asked') || '{}');
        for (const k of Object.keys(map)) {
            if (now - map[k] > REKEY_COOLDOWN_MS) delete map[k];
        }
        const recent = map[key] && (now - map[key] < REKEY_COOLDOWN_MS);
        if (!recent) map[key] = now;
        localStorage.setItem('e2ee_rekey_asked', JSON.stringify(map));
        return !recent;
    } catch (_) {
        return true; // en cas d'erreur storage, ne pas bloquer la récupération
    }
}

socket.on('historique-prive', async (messages) => {
    const messagesUl = document.getElementById('messages');
    messagesUl.innerHTML = '';
    calculerPremierNonVu(messages, cleConversation(null, state.destinataireID));

    // (Ré)initialise l'état de pagination pour cette conversation.
    _mpHist.avec = state.destinataireID;
    _mpHist.loading = false;
    _mpHist.done = messages.length < 100;   // < une page pleine = plus rien au-dessus
    _mpHist.oldest = messages.length ? messages[0].date_envoi : null;
    _assurerScrollPaginationMP();

    await _preparerMessagesHistorique(messages);
    for (const msg of messages) ajouterMessageAuDOM(msg);
});

socket.on('historique-prive-page', async ({ avec, messages } = {}) => {
    const messagesUl = document.getElementById('messages');
    if (!messagesUl || avec !== state.destinataireID) { _mpHist.loading = false; return; }
    if (!Array.isArray(messages) || messages.length === 0) {
        _mpHist.done = true; _mpHist.loading = false; return;
    }

    await _preparerMessagesHistorique(messages);

    // Préfixe la page au-dessus de l'existant en conservant la position visuelle.
    const prevH   = messagesUl.scrollHeight;
    const prevTop = messagesUl.scrollTop;
    const anchor  = messagesUl.firstElementChild;
    for (const msg of messages) ajouterMessageAuDOM(msg, { prepend: true, anchor });
    messagesUl.scrollTop = prevTop + (messagesUl.scrollHeight - prevH);

    _mpHist.oldest  = messages[0].date_envoi;
    _mpHist.done    = messages.length < 100;
    _mpHist.loading = false;
});

function _retirerPlaceholdersE2ee(auteur) {
    document.querySelectorAll(`.message-item[data-e2ee-placeholder="${CSS.escape(auteur)}"]`).forEach(el => el.remove());
}

// Sérialise le déchiffrement E2EE : évite la race condition type-0/type-1
// quand l'expéditeur envoie plusieurs messages avant que la session soit établie.
let _e2eeDecryptQueue = Promise.resolve();

socket.on('chat-mp-e2ee', (data) => {
    _e2eeDecryptQueue = _e2eeDecryptQueue.catch(() => {}).then(async () => {
    const senderDeviceId = data.device_expediteur;
    try {
        if (!senderDeviceId) throw new Error('device_expediteur manquant dans le payload.');

        const texte = await ChaosMPCrypto.dechiffrerMessage(
            data.contenu_chiffre, data.auteur,
            senderDeviceId, null
        );
        await ChaosKeystore.saveMessage({
            id: String(data.id), auteur: data.auteur,
            interlocuteur: data.auteur, texte,
            date_envoi: Date.now(),
            message_logical_id: data.message_logical_id
        });
        if (window.ChaosSessionBackup) {
            ChaosSessionBackup.planifierSauvegarde(senderDeviceId, data.auteur, socket);
            if (data.message_logical_id) {
                ChaosSessionBackup.sauvegarderEnvoi(data.message_logical_id, data.auteur, texte, socket).catch(() => {});
            }
        }

        if (state.destinataireID === data.auteur || data.auteur === state.monPseudo) {
            _retirerPlaceholdersE2ee(data.auteur);
            ajouterMessageAuDOM({ ...data, texte }, { playEffect: true });
        }
        if (state.destinataireID !== data.auteur && data.auteur !== state.monPseudo) {
            if (state.notifications) state.notifications[data.auteur] = (state.notifications[data.auteur] || 0) + 1;
            window.gererBulleMP(data.auteur, data.couleur_bdd);
            if (getNotifPref('mp')) {
                const monBadge = document.getElementById('self-status-badge');
                if (monBadge && !monBadge.classList.contains('status-occupe')) {
                    notifSound.play().catch(() => {});
                    _notifNative(data.auteur, texte, () => window.ouvrirChatPrive && window.ouvrirChatPrive(data.auteur));
                }
            }
            majBadgeNatif();
        }
    } catch (err) {
        console.error('[E2EE] Déchiffrement message entrant :', err.message);

        if (window.ChaosSessionBackup && data.message_logical_id) {
            try {
                const msgBackups = await ChaosSessionBackup.restaurerEnvois(data.auteur, socket);
                const fromBackup = msgBackups.get(data.message_logical_id);
                if (fromBackup) {
                    await ChaosKeystore.saveMessage({
                        id: String(data.id), auteur: data.auteur,
                        interlocuteur: data.auteur, texte: fromBackup,
                        date_envoi: Date.now(),
                        message_logical_id: data.message_logical_id
                    });
                    if (state.destinataireID === data.auteur || data.auteur === state.monPseudo) {
                        _retirerPlaceholdersE2ee(data.auteur);
                        ajouterMessageAuDOM({ ...data, texte: fromBackup });
                    }
                    if (state.destinataireID !== data.auteur && data.auteur !== state.monPseudo) {
                        if (state.notifications) state.notifications[data.auteur] = (state.notifications[data.auteur] || 0) + 1;
                        window.gererBulleMP(data.auteur, data.couleur_bdd);
                        if (getNotifPref('mp')) {
                            const monBadge = document.getElementById('self-status-badge');
                            if (monBadge && !monBadge.classList.contains('status-occupe')) {
                                notifSound.play().catch(() => {});
                                _notifNative(data.auteur, fromBackup, () => window.ouvrirChatPrive && window.ouvrirChatPrive(data.auteur));
                            }
                        }
                        majBadgeNatif();
                    }
                    return;
                }
            } catch (_) { /* pas grave, on retombe sur le rekey */ }
        }

        const _rekeyInProgress = window._rekeyInProgress = window._rekeyInProgress || new Set();
        const rekeyKey = data.auteur + ':' + (senderDeviceId || '');
        if (senderDeviceId && !_rekeyInProgress.has(rekeyKey)) {
            _rekeyInProgress.add(rekeyKey);
            setTimeout(() => _rekeyInProgress.delete(rekeyKey), 20000);
            window.ChaosTelemetry?.report('e2ee_live_echec', {
                message: 'message live indéchiffrable → rekey demandé',
                peer: data.auteur, device: (senderDeviceId || '').slice(0, 8),
                logicalId: data.message_logical_id || null, path: 'live'
            });

            if (state.destinataireID === data.auteur || data.auteur === state.monPseudo) {
                ajouterMessageAuDOM({ ...data, texte: '🔄 *(resynchronisation du tunnel E2EE…)*', _isE2eePlaceholder: true });
            }
            if (state.destinataireID !== data.auteur && data.auteur !== state.monPseudo) {
                if (state.notifications) state.notifications[data.auteur] = (state.notifications[data.auteur] || 0) + 1;
                window.gererBulleMP(data.auteur, data.couleur_bdd);
                if (getNotifPref('mp')) {
                    const monBadge = document.getElementById('self-status-badge');
                    if (monBadge && !monBadge.classList.contains('status-occupe')) {
                        notifSound.play().catch(() => {});
                        // Contenu pas encore déchiffré (resync en cours) → notif sans aperçu.
                        _notifNative(data.auteur, '', () => window.ouvrirChatPrive && window.ouvrirChatPrive(data.auteur));
                    }
                }
                majBadgeNatif();
            }

            (async () => {
                try {
                    if (err.message?.includes('BAD_MESSAGE_KEY_ID')) {
                        await ChaosMPCrypto.rafraichirOTKs(socket);
                    }
                    const myDeviceId = await ChaosKeystore.getMeta('device_id');
                    await ChaosMPCrypto.effacerSession(data.auteur, senderDeviceId);
                    socket.emit('e2ee-rekey-request', {
                        target: data.auteur,
                        target_device_id: senderDeviceId,
                        message_logical_id: data.message_logical_id,
                        requester_device_id: myDeviceId
                    });
                } catch (repairErr) {
                    console.error('[E2EE] Réparation automatique échouée :', repairErr.message);
                }
            })();
        } else {
            if (state.destinataireID === data.auteur || data.auteur === state.monPseudo) {
                ajouterMessageAuDOM({ ...data, texte: '⚠️ *(message non déchiffrable)*', _isE2eePlaceholder: true });
            }
            if (state.destinataireID !== data.auteur && data.auteur !== state.monPseudo) {
                window.gererBulleMP(data.auteur, '#f85149');
            }
        }
    }
    }); // fin _e2eeDecryptQueue
});

socket.on('chat-mp-e2ee-other-device', async (data) => {
    try {
        const sbk = await ChaosKeystore.getMeta('session_backup_key');
        if (!sbk) {
            console.warn('[E2EE] SBK absente sur ce device — message non déchiffrable. Entre ton PIN à la prochaine connexion.');
            return;
        }
        if (!data.encrypted_content) {
            console.warn('[E2EE] chat-mp-e2ee-other-device reçu sans encrypted_content.');
            return;
        }
        const texte = await ChaosSessionBackup.dechiffrerAvecSBK(data.encrypted_content, sbk);
        await ChaosKeystore.saveMessage({
            id: String(data.id),
            auteur: state.monPseudo,
            interlocuteur: data.destinataire,
            texte,
            date_envoi: Date.now(),
            sent: true,
            message_logical_id: data.message_logical_id
        });
        if (state.destinataireID === data.destinataire) {
            ajouterMessageAuDOM({
                id: data.id,
                auteur: state.monPseudo,
                texte,
                destinataire: data.destinataire,
                date_envoi: data.date_envoi || new Date(),
                couleur_bdd: data.couleur_bdd || state.maCouleur,
                e2ee: true
            });
        }
    } catch (err) {
        console.error('[E2EE] Échec déchiffrement SBK pour message cross-device (probable mismatch SBK entre devices) :', err.message);
    }
});

socket.on('chat-mp-e2ee-sent', async (data) => {
    if (!data.message_logical_id || !data.destinataire) return;
    try {
        const msgs = await ChaosKeystore.searchMessages(data.destinataire, null);
        const local = msgs.find(m => m.sent && m.message_logical_id === data.message_logical_id);
        if (local && local.id !== String(data.id)) {
            await ChaosKeystore.saveMessage({ ...local, id: String(data.id) });
        }
    } catch (e) { /* non bloquant */ }
});

// Sérialisé : les rekey-needed arrivent en rafale (un par message à récupérer).
// S'ils s'exécutaient en parallèle, leurs effacerSession/chiffrerPourPair se
// marchaient dessus (event B chargeait la session fraîche que event A venait de
// sauvegarder → message type-1 au lieu de type-0 → indéchiffrable depuis
// l'historique). On les traite donc UN PAR UN.
let _rekeyResponderQueue = Promise.resolve();
socket.on('e2ee-rekey-needed', (args) => {
    _rekeyResponderQueue = _rekeyResponderQueue.catch(() => {}).then(() => _traiterRekeyNeeded(args || {}));
});
async function _traiterRekeyNeeded({ requester, requester_device_id, message_logical_id } = {}) {
    if (!window.ChaosMPCrypto) return;
    const _rekeyResponderSeen = window._rekeyResponderSeen = window._rekeyResponderSeen || new Set();
    const dedupKey = requester + ':' + (message_logical_id || '');
    if (message_logical_id && _rekeyResponderSeen.has(dedupKey)) {
        return;
    }
    if (message_logical_id) {
        _rekeyResponderSeen.add(dedupKey);
        setTimeout(() => _rekeyResponderSeen.delete(dedupKey), 20000);
    }
    try {
        await ChaosMPCrypto.effacerSession(requester, requester_device_id);

        const fromBuffer = window._rekeyBuffer?.get(message_logical_id);
        let clair = (fromBuffer && fromBuffer.destinataire === requester) ? fromBuffer.texte : null;
        let clairSrc = clair !== null ? 'buffer' : null;

        if (clair === null) {
            try {
                const stored = await ChaosKeystore.getSentByLogicalId(message_logical_id);
                if (stored && stored.interlocuteur === requester) { clair = stored.texte; clairSrc = 'IndexedDB'; }
            } catch (e) {
                console.warn('[E2EE] Lookup IndexedDB rekey échoué :', e.message);
            }
        }

        // Récupérer TOUS les backups SBK pour ce pair — serviront à la fois pour le
        // message demandé ET pour le batch de re-livraison (ci-dessous).
        let sbkBackups = new Map();
        let sbk = null;
        if (window.ChaosSessionBackup) {
            sbk = await ChaosKeystore.getMeta('session_backup_key');
            if (!sbk) {
                console.warn('[E2EE] Rekey SBK impossible — session_backup_key absent (PIN non validé ?)');
            } else {
                sbkBackups = await ChaosSessionBackup.restaurerEnvois(requester, socket);
            }
        }

        if (clair === null && message_logical_id) {
            const fromSBK = sbkBackups.get(message_logical_id);
            if (fromSBK) {
                clair = fromSBK; clairSrc = 'SBK';
                ChaosKeystore.saveMessage({
                    id: 'sbk-' + message_logical_id,
                    auteur: state.monPseudo, interlocuteur: requester,
                    texte: clair, date_envoi: Date.now(), sent: true, message_logical_id
                }).catch(() => {});
            } else if (sbkBackups.size > 0) {
                console.warn(`[E2EE] Rekey SBK — logical_id ${message_logical_id?.slice(0,8)} introuvable dans les ${sbkBackups.size} backups`);
            }
        }

        const myDeviceId = await ChaosKeystore.getMeta('device_id');

        // Envoyer le message demandé (si trouvé)
        if (clair !== null && clair !== undefined) {
            // Session fraîche juste avant → type-0 autonome (re-effacée ici car un
            // rekey concurrent a pu ré-établir la session pendant l'attente SBK).
            if (requester_device_id) await ChaosMPCrypto.effacerSession(requester, requester_device_id);
            const result = await ChaosMPCrypto.chiffrerPourPair(clair, requester, socket);
            if (result) {
                const encContent = sbk ? await ChaosSessionBackup.chiffrerAvecSBK(clair, sbk) : null;
                socket.emit('chat-mp-e2ee', {
                    destinataire: requester, envelopes: result.envelopes,
                    device_expediteur: myDeviceId,
                    message_logical_id: message_logical_id || crypto.randomUUID(),
                    sbk_backup: encContent ? { encrypted_content: encContent } : null,
                    is_rekey: true
                });
            }
        } else {
            console.warn(`[E2EE] Rekey ÉCHEC pour ${requester} — clair introuvable après buffer+IndexedDB+SBK (msg ${message_logical_id?.slice(0,8)})`);
        }

        // Batch re-livraison : re-envoyer tous les autres backups SBK en une passe.
        // CRUCIAL : on EFFACE la session avec l'appareil demandeur AVANT chaque
        // message → chiffrerPourPair recrée une session fraîche → message de
        // TYPE-0 (prekey, autonome). Sans ça, les messages suivants réutilisaient
        // la session ratchetée → TYPE-1, indéchiffrables depuis l'historique (un
        // type-1 a besoin de l'état de session que Rachel n'a pas après reload →
        // BAD_MESSAGE_MAC). Coût : une OTK/fallback par message (acceptable, c'est
        // de la récupération ; la fallback key couvre l'épuisement). Limité à 40.
        // Dédup par pair pour ne pas re-livrer le batch à chaque rekey de ce pair.
        if (sbkBackups.size > 0) {
            const _batchSent = window._rekeyBatchSent = window._rekeyBatchSent || new Set();
            const batchKey = requester;
            if (!_batchSent.has(batchKey)) {
                _batchSent.add(batchKey);
                setTimeout(() => _batchSent.delete(batchKey), 60000); // reset après 60s
                let batchCount = 0;
                const BATCH_LIMIT = 40;
                // Itérer du plus récent au plus ancien (Map conserve l'ordre d'insertion = ASC date)
                const entries = [...sbkBackups.entries()].reverse();
                for (const [logId, text] of entries) {
                    if (logId === message_logical_id) continue;
                    if (batchCount >= BATCH_LIMIT) break;
                    try {
                        // Session fraîche → type-0 autonome (récupérable depuis l'historique).
                        if (requester_device_id) await ChaosMPCrypto.effacerSession(requester, requester_device_id);
                        const res = await ChaosMPCrypto.chiffrerPourPair(text, requester, socket);
                        if (!res) continue;
                        const encC = sbk ? await ChaosSessionBackup.chiffrerAvecSBK(text, sbk) : null;
                        socket.emit('chat-mp-e2ee', {
                            destinataire: requester, envelopes: res.envelopes,
                            device_expediteur: myDeviceId, message_logical_id: logId,
                            sbk_backup: encC ? { encrypted_content: encC } : null,
                            is_rekey: true
                        });
                        batchCount++;
                    } catch (_) {}
                }
            }
        }
    } catch (err) {
        console.error('[E2EE] Erreur lors du rekey :', err.message);
        if (err.message?.includes('Aucune one-time key')) {
            socket.emit('e2ee-otk-needed', { target: requester, message_logical_id });
        }
    }
}

socket.on('e2ee-please-refresh-otks', async ({ requester, message_logical_id }) => {
    if (!window.ChaosMPCrypto) return;
    try {
        await ChaosMPCrypto.rafraichirOTKs(socket);
        const myDeviceId = await ChaosKeystore.getMeta('device_id');
        socket.emit('e2ee-rekey-request', {
            target: requester,
            message_logical_id,
            requester_device_id: myDeviceId
        });
    } catch (e) {
        console.error('[E2EE] Impossible de rafraîchir OTKs sur demande :', e.message);
    }
});

// ==========================================================================
//   INDICATEUR D'ÉCRITURE / ENREGISTREMENT
// ==========================================================================

let autoClearTyping;
socket.on('user-typing', (data) => {
    const ind = document.getElementById('typing-indicator');
    if (!ind) return;
    const estBonSalon =
        (data.prive && state.destinataireID === data.auteur) ||
        (!data.prive && data.salonId && data.salonId == state.salonActuelID) ||
        (!data.prive && !data.salonId && !state.salonActuelID && !state.destinataireID && !state.sphereActuelleID);
    if (estBonSalon) {
        ind.textContent = t('chat.typing', {author: data.auteur});
        ind.style.color = "#8b949e";
        ind.style.fontStyle = "italic";
        ind.style.fontSize = "0.85em";
        clearTimeout(autoClearTyping);
        autoClearTyping = setTimeout(() => { ind.textContent = ''; }, 3000);
    }
});

socket.on('user-stop-typing', () => {
    clearTimeout(autoClearTyping);
    document.getElementById('typing-indicator').textContent = '';
});

let autoClearRecording;
socket.on('user-recording', (data) => {
    const ind = document.getElementById('typing-indicator');
    if (!ind) return;
    const estBonSalon =
        (data.prive && state.destinataireID === data.auteur) ||
        (!data.prive && data.salonId && data.salonId == state.salonActuelID) ||
        (!data.prive && !data.salonId && !state.salonActuelID && !state.destinataireID && !state.sphereActuelleID);
    if (estBonSalon) {
        ind.textContent = `🎙️ ${data.auteur} enregistre un message vocal…`;
        ind.style.color = "#8b949e";
        ind.style.fontStyle = "italic";
        ind.style.fontSize = "0.85em";
        clearTimeout(autoClearRecording);
        autoClearRecording = setTimeout(() => { ind.textContent = ''; }, 3000);
    }
});

socket.on('user-stop-recording', () => {
    clearTimeout(autoClearRecording);
    document.getElementById('typing-indicator').textContent = '';
});

// ==========================================================================
//   RECHERCHE DANS LES MESSAGES
// ==========================================================================

export function afficherResultats(resultats, query, auteur, type) {
    const panel = document.getElementById('search-results-panel');
    const list  = document.getElementById('srp-list');
    const title = document.getElementById('srp-title');
    if (!panel) return;

    const labels = [];
    if (query)  labels.push(`"${query}"`);
    if (auteur) labels.push(`@${auteur}`);
    if (type)   labels.push(type.toUpperCase());
    title.textContent = `${t('search.results', {count: resultats.length})}  ${labels.join(' · ')}`;

    list.innerHTML = '';
    if (resultats.length === 0) {
        const vide = document.createElement('p');
        vide.className = 'srp-empty';
        vide.textContent = t('search.no_results');
        list.appendChild(vide);
    } else {
        resultats.forEach(({ li, auteurTexte, couleur, heureTexte, extrait, query: q }) => {
            const card = document.createElement('div');
            card.className = 'srp-card';

            let extraitHtml = extrait.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            if (q) {
                const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                extraitHtml = extraitHtml.replace(re, m => `<mark class="search-mark">${m}</mark>`);
            }

            card.innerHTML = `
                <div class="srp-meta">
                    <span class="srp-author" style="color:${couleur}">${auteurTexte}</span>
                    <span class="srp-time">${heureTexte}</span>
                </div>
                <div class="srp-excerpt">${extraitHtml}</div>
                <button class="srp-goto-btn" data-i18n="search.goto">${t('search.goto')}</button>
            `;
            card.querySelector('.srp-goto-btn').addEventListener('click', () => allerAuMessage(li));
            list.appendChild(card);
        });
    }

    panel.classList.add('is-open');
}

export function fermerResultats() {
    document.getElementById('search-results-panel')?.classList.remove('is-open');
}

function allerAuMessage(li) {
    if (!li) return;
    li.scrollIntoView({ behavior: 'smooth', block: 'center' });
    li.classList.add('search-flash');
    setTimeout(() => li.classList.remove('search-flash'), 2200);
}

// ==========================================================================
//   CONVERSATION — dernier vu + premier non-lu
// ==========================================================================

let premierNonVuId = null;

function cleConversation(salonId, pseudoId) {
    if (salonId) return `chaos_lastSeen_salon_${salonId}`;
    if (pseudoId) return `chaos_lastSeen_mp_${pseudoId}`;
    return `chaos_lastSeen_general`;
}

export function sauvegarderDernierVu() {
    const cle = cleConversation(state.salonActuelID, state.destinataireID);
    const messagesUl = document.getElementById('messages');
    const items = messagesUl ? messagesUl.querySelectorAll('.message-item[data-msg-id]') : [];
    if (items.length > 0) {
        localStorage.setItem(cle, items[items.length - 1].dataset.msgId);
    }
}

function calculerPremierNonVu(messages, cle) {
    const lastSeenId = localStorage.getItem(cle);
    premierNonVuId = null;
    if (lastSeenId && messages.length > 0) {
        const idx = messages.findIndex(m => String(m.id) === String(lastSeenId));
        if (idx >= 0 && idx < messages.length - 1) {
            const premierAutre = messages.slice(idx + 1).find(m => m.auteur !== state.monPseudo);
            if (premierAutre) premierNonVuId = String(premierAutre.id);
        }
    }
    if (messages.length > 0) {
        localStorage.setItem(cle, String(messages[messages.length - 1].id));
    }
}

// ==========================================================================
//   MENU CONTEXTUEL DES MESSAGES
// ==========================================================================

export async function actionMessage(action) {
    const msg = window.msgContextuelActuel;
    if (!msg) return;

    document.getElementById('message-context-menu').style.display = 'none';

    switch (action) {
        case 'reaction':
            setReactionMessageId(msg.id);
            const fullMenu = document.getElementById('full-emoji-picker-container');
            fullMenu.style.position = 'fixed';
            fullMenu.style.zIndex = '4000';
            fullMenu.style.top = '50%';
            fullMenu.style.left = '50%';
            fullMenu.style.transform = 'translate(-50%, -50%)';
            setTimeout(() => { fullMenu.style.display = 'block'; }, 10);
            break;

        case 'repondre':
            repondre(msg.auteur, msg.texte);
            break;

        case 'copier':
            navigator.clipboard.writeText(msg.texte);
            popToast(t('toast.text_copied'));
            break;

        case 'modifier':
            commencerEdition(msg);
            break;

        case 'supprimer':
            const confirmDel = await chaosNotify(t('confirm.delete_msg'), t('confirm.delete_msg_title'), "confirm");
            if (confirmDel) {
                const lid = msg.message_logical_id || null;
                socket.emit('supprimer-message', { id: msg.id, message_logical_id: lid });
                try {
                    if (lid && window.ChaosKeystore?.deleteByLogicalId) {
                        await ChaosKeystore.deleteByLogicalId(lid);
                    }
                    if (window.ChaosKeystore?.deleteMessageById) {
                        await ChaosKeystore.deleteMessageById(String(msg.id));
                    }
                } catch (_) { /* non bloquant */ }
                if (lid) {
                    document.querySelectorAll(`.message-item[data-logical-id="${CSS.escape(lid)}"]`).forEach(el => el.remove());
                }
                popToast(t('toast.msg_deleted'));
            }
            break;

        case 'signaler':
            ouvrirModalReport(msg.id);
            break;

        case 'bannir':
            if (msg.auteur && typeof window.ouvrirBanModal === 'function') {
                window.ouvrirBanModal(msg.auteur, state.sphereActuelleID, false);
            }
            break;

        case 'transferer':
            popToast(t('toast.transfer_wip'));
            break;

        case 'epingler':
            socket.emit('toggle-epingle', {
                messageId: msg.id,
                salonId: state.salonActuelID,
                destinataire: state.destinataireID
            });
            break;

        case 'non_lu':
            popToast(t('toast.unread_wip'));
            break;
    }
}

// ==========================================================================
//   ÉPINGLES
// ==========================================================================

export function togglePinsPanel() {
    const panel = document.getElementById('pins-side-panel');
    panel.classList.toggle('open');

    if (panel.classList.contains('open')) {
        if (state.salonActuelID || state.destinataireID) {
            socket.emit('demander-epingles', {
                salonId: state.salonActuelID,
                destinataire: state.destinataireID
            });
        } else {
            document.getElementById('pins-list-container').innerHTML = `<p style='color: #8b949e; text-align: center; margin-top: 20px;'>${t('pins.empty_channel')}</p>`;
        }
    }
}

socket.on('maj-epingles-dom', (data) => {
    const panel = document.getElementById('pins-side-panel');
    const estBonSalon = (data.salonId && data.salonId == state.salonActuelID);
    const estBonMP = (!data.salonId && (data.destinataire == state.destinataireID || data.auteur == state.destinataireID));

    if (estBonSalon || estBonMP) {
        if (panel && panel.classList.contains('open')) {
            socket.emit('demander-epingles', { salonId: state.salonActuelID, destinataire: state.destinataireID });
        }

        const actionText = data.estEpingle ? "a épinglé un message" : "a retiré une épingle";
        const infoMsg = `📌 ${data.auteur} ${actionText} dans cette dimension.`;

        const li = document.createElement('li');
        li.className = 'message-system';
        li.style.fontStyle = 'italic';
        li.style.color = '#8b949e';
        li.style.textAlign = 'center';
        li.style.margin = '10px 0';
        li.style.fontSize = '0.85em';
        li.textContent = infoMsg;

        document.getElementById('messages').appendChild(li);
        scrollToBottom();
    }
});

// ==========================================================================
//   MESSAGES ÉPHÉMÈRES — countdown + expiration
// ==========================================================================

function _formatCountdown(msLeft) {
    if (msLeft <= 0) return '0s';
    const s = Math.ceil(msLeft / 1000);
    if (s < 60)  return `${s}s`;
    const m = Math.ceil(s / 60);
    if (m < 60)  return `${m}min`;
    const h = Math.ceil(m / 60);
    if (h < 24)  return `${h}h`;
    return `${Math.ceil(h / 24)}j`;
}

function _majCountdownsEphemeres() {
    const badges = document.querySelectorAll('.ephemere-badge[data-expire]');
    const now = Date.now();
    badges.forEach(badge => {
        const expireTs = Number(badge.dataset.expire);
        const msLeft = expireTs - now;
        const span = badge.querySelector('.ephemere-countdown');
        if (span) span.textContent = _formatCountdown(msLeft);
    });
}
setInterval(_majCountdownsEphemeres, 5_000);
_majCountdownsEphemeres();

socket.on('message-ephemere-expire', ({ id, logical_id, salonId, destinataire }) => {
    const messagesUl = document.getElementById('messages');
    if (!messagesUl) return;

    // Vérifier que ce message concerne le contexte actuellement affiché
    if (salonId) {
        if (String(state.salonActuelID) !== String(salonId)) return;
    } else if (destinataire && destinataire !== 'public') {
        const implique = destinataire === state.monPseudo || destinataire === state.destinataireID;
        if (!implique) return;
    }

    // Supprimer par id numérique ou logical_id
    if (id) {
        const el = messagesUl.querySelector(`.message-item[data-msg-id="${CSS.escape(String(id))}"]`);
        if (el) { _recalcGroupeNoeud(el.nextElementSibling); el.remove(); return; }
    }
    if (logical_id) {
        const el = messagesUl.querySelector(`.message-item[data-logical-id="${CSS.escape(logical_id)}"]`);
        if (el) { _recalcGroupeNoeud(el.nextElementSibling); el.remove(); }
    }
});
